<script setup lang="ts">
import { useHead } from '@vueuse/head';
import unescape from 'lodash/unescape';
import { onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';

import { useJobPostings } from '@/composables/useJobPostings';
import { useLayout } from '@/stores/layout';

const route = useRoute();
const { loadJobs, selectedJob, sortBy, sortedJobGroups } = useJobPostings(route);

onMounted(loadJobs);

const layoutStore = useLayout();
layoutStore.displayFooter = false;
layoutStore.displayHeader = false;
onUnmounted(() => {
  layoutStore.displayFooter = true;
  layoutStore.displayHeader = true;
});
defineOptions({
  criticalData() {
    const criticalLayoutStore = useLayout();
    criticalLayoutStore.displayFooter = false;
    criticalLayoutStore.displayHeader = false;
  },
});

const scrollToTop = () => {
  window.scrollTo(0, 0);
};

const description =
  "Our family has been in the nut business since 1929, and we're growing faster than ever! Nuts about a fulfilling career? Join us!";
useHead({
  title: 'Work with Nuts!',
  meta: [
    {
      name: 'description',
      content: description,
    },
    {
      property: 'og:title',
      content: 'Careers | Join us | Nuts.com',
      key: 'og:title',
    },
    {
      property: 'og:type',
      content: 'Website',
      key: 'og:type',
    },
    {
      property: 'og:url',
      content: 'https://nuts.com/careers',
      key: 'og:url',
    },
    {
      property: 'og:image',
      content: 'https://nuts.com/images/email/2024/careers_people.2f0f957d.jpg',
      key: 'og:image',
    },
    {
      property: 'og:description',
      content: description,
      key: 'og:description',
    },
  ],
});

const logos = {
  bloomberg: nutshell['static_img/jobs/media/bloomberg.png'],
  brides: nutshell['static_img/media/brides.png'],
  chow: nutshell['static_img/media/chow.png'],
  cnbc: nutshell['static_img/media/cnbc.png'],
  entrepreneur: nutshell['static_img/jobs/media/entrepreneur.png'],
  forbes: nutshell['static_img/jobs/media/forbes.png'],
  fortune: nutshell['static_img/jobs/media/fortune.png'],
  glamour: nutshell['static_img/jobs/media/glamour.png'],
  health: nutshell['static_img/media/health.png'],
  hostess: nutshell['static_img/media/hostess.png'],
  inc: nutshell['static_img/jobs/media/inc.png'],
  inStyle: nutshell['static_img/jobs/media/instyle.png'],
  marthaStewart: nutshell['static_img/media/martha-stewart.png'],
  marthaStewartLiving: nutshell['static_img/media/martha-stewart-living.png'],
  muscleAndFitness: nutshell['static_img/jobs/media/muscle-and-fitness.png'],
  nyTimes: nutshell['static_img/media/nytimes.png'],
  pbs: nutshell['static_img/media/pbs.png'],
  prevention: nutshell['static_img/jobs/media/prevention.png'],
  rachelRay: nutshell['static_img/media/rachel-ray.png'],
  sheKnows: nutshell['static_img/media/she-knows.png'],
  site: nutshell['img/nuts-logo-white.svg'],
  starLedger: nutshell['static_img/media/star-ledger.png'],
  time: nutshell['static_img/jobs/media/time.png'],
};
</script>

<template>
  <div class="page-careers-styles">
    <header class="clearfix">
      <h1 class="header-logo">
        <RouterLink to="/"><img :src="logos.site" alt="Nuts.com" /></RouterLink>
        <RouterLink to="/careers" class="ml-1" @click="scrollToTop"
          ><span>Careers</span></RouterLink
        >
      </h1>
      <div v-if="selectedJob" class="header-apply">
        <a class="button primary" id="header-apply" :href="`${selectedJob.absolute_url}#app`"
          >Apply&nbsp;Now</a
        >
      </div>
    </header>

    <div class="splash jobs"><div /></div>

    <div class="container">
      <div v-if="selectedJob" class="positions section">
        <p class="return">
          <RouterLink to="/careers" class="button default" @click="scrollToTop">
            Back to all positions
          </RouterLink>
        </p>
        <h1>{{ selectedJob.title }}</h1>
        <p class="tags">
          <span>{{ selectedJob.location.name }}</span>
          <span>{{ selectedJob.departments[0].name }}</span>
        </p>
        <div class="section" v-html="unescape(selectedJob.content)" />
        <p class="apply">
          <a class="button primary" :href="`${selectedJob.absolute_url}#app`">Apply Now</a>
        </p>
      </div>

      <template v-else>
        <div class="listing-page">
          <div class="section">
            <div class="nutty-intro section">
              <p>The Nuts.com team is:</p>
              <div class="row">
                <div class="col-sm-6">
                  <ul>
                    <li>✔&nbsp; Fast-paced</li>
                    <li>✔&nbsp; Driven</li>
                    <li>✔&nbsp; Collaborative</li>
                  </ul>
                </div>
                <div class="col-sm-6">
                  <ul>
                    <li>✔&nbsp; Growing at sonic speed</li>
                    <li>✔&nbsp; Fun and nutty</li>
                  </ul>
                </div>
              </div>
            </div>
            <p>
              Our family has been in the nut business since 1929, and we're growing faster than
              ever! We're expanding our teams on both coasts as we continue our mission to deliver
              the highest-quality products and exceptional service to millions of happy customers
              across the U.S. and internationally.
            </p>
            <p>
              We're looking for ambitious, talented, and passionate individuals to do what they do
              best, with us! If you want to work for a company where you can contribute your
              talents, build a great brand, and grow in your career, you've got our number (or, more
              importantly, our "apply" button)!
            </p>
            <p>
              Since we want our employees to feel like they're part of our nutty family, we offer
              many competitive benefits, including a 401k match and free Nuts.com snacks!
            </p>
          </div>

          <a name="positions" />
          <h2>Open Positions</h2>
          <p class="clearfix">
            <span class="sort-title">Sort positions by:</span>
            <span class="sort-groups">
              <button
                id="location"
                class="button default"
                :class="{ selected: sortBy === 'location' }"
                @click="sortBy = 'location'"
              >
                Location
              </button>
              <button
                id="team"
                class="button default"
                :class="{ selected: sortBy === 'team' }"
                @click="sortBy = 'team'"
              >
                Team
              </button>
            </span>
          </p>
        </div>

        <div id="jobs-list" class="positions section">
          <div v-for="{ key, jobs } in sortedJobGroups" :key>
            <div class="group">
              <h3>{{ key }}</h3>
              <div v-for="job in jobs" :key="job.id" class="job">
                <div class="clearfix">
                  <h4>
                    <RouterLink
                      :to="`/careers?position=${job.id}`"
                      class="text-black job-title"
                      @click="scrollToTop"
                    >
                      {{ job.title }}
                    </RouterLink>
                  </h4>
                  <span class="learn-more"
                    ><RouterLink
                      :to="`/careers?position=${job.id}`"
                      class="text-black button primary compact"
                      @click="scrollToTop"
                      >Learn more</RouterLink
                    ></span
                  >
                </div>
                <div class="tags list">
                  <span>{{ job.location.name }}</span>
                  <span>{{ job.departments[0].name }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <div v-show="!selectedJob" class="listing-page logos">
      <h3>You might have seen our nutty antics on the tube and in the&nbsp;papers!</h3>
      <img :src="logos.rachelRay" alt="Rachel Ray Show" />
      <img :src="logos.marthaStewart" alt="Martha Stewart Show" />
      <img :src="logos.pbs" alt="PBS" />
      <img :src="logos.brides" alt="Brides.com" />
      <img :src="logos.marthaStewartLiving" alt="Martha Stewart Magazine" />
      <img :src="logos.nyTimes" alt="New York Times" />
      <img :src="logos.starLedger" alt="New Jersey Star Ledger" />
      <img :src="logos.sheKnows" alt="SheKnows" />
      <img :src="logos.cnbc" alt="CNBC" />
      <img :src="logos.health" alt="Health Magazine" />
      <img :src="logos.hostess" alt="Hostess with the Mostess" />
      <img :src="logos.chow" alt="Chow.com" />
      <img :src="logos.bloomberg" alt="Bloomberg" />
      <img :src="logos.entrepreneur" alt="Entrepreneur" />
      <img :src="logos.forbes" alt="Forbes" />
      <img :src="logos.fortune" alt="Fortune" />
      <img :src="logos.glamour" alt="Glamour" />
      <img :src="logos.inc" alt="Inc" />
      <img :src="logos.inStyle" alt="InStyle" />
      <img :src="logos.muscleAndFitness" alt="Muscle and Fitness" />
      <img :src="logos.prevention" alt="Prevention" />
      <img :src="logos.time" alt="Time" />
    </div>

    <div v-if="!selectedJob" class="text-center text-xs max-w-5xl mx-auto px-[15px] py-[25px]">
      <p>
        Nuts.com is an equal opportunity employer. All qualified applicants will receive
        consideration for employment without regard of actual or perceived race, color, creed,
        religion, national origin, ancestry, citizenship status, immigration status, age, sex or
        gender (including pregnancy), gender identity or expression (including transgender status),
        sexual orientation, marital status, veteran status, physical or mental disability, genetic
        information, or any other characteristic protected by applicable federal, state, or local
        laws.
      </p>
      <p>
        <em>
          Applicants with disabilities who require assistance or accommodation during the
          application or interview process should reach out to us at
          <a href="mailto:people@nuts.com" class="text-nuts-cyan-600">people@nuts.com</a>.
        </em>
      </p>
      <div class="flex justify-center gap-12 md:gap-24">
        <a
          href="https://www.justice.gov/crt/case-document/file/1133936/dl?inline"
          target="_blank"
          class="text-nuts-cyan-600"
        >
          IER Right to Work - English
        </a>
        <a
          href="https://www.justice.gov/crt/case-document/file/1133941/dl?inline"
          target="_blank"
          class="text-nuts-cyan-600"
        >
          IER Right to Work - Spanish
        </a>
      </div>
    </div>
  </div>
</template>

<style>
.page-careers-styles {
  padding: 0 0 40px 0;
  margin: 0;
  font-family: proxima-nova, sans-serif;
  font-size: 15px;
  line-height: 1.4;
  color: #333;

  i,
  em {
    font-style: italic;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: sofia-pro, sans-serif;
    font-style: normal;
    font-weight: 600;
  }

  h1 {
    font-size: 32px;
    margin: 30px 0 10px 0;
    text-align: center;
  }

  h2 {
    font-size: 30px;
    margin-bottom: 32px;
    text-align: center;

    @media screen and (max-width: 649px) {
      font-size: 28px;
    }
  }

  h3 {
    font-size: 26px;
    margin-bottom: 15px;

    @media screen and (max-width: 649px) {
      font-size: 24px;
      text-align: center;
    }
  }

  h4 {
    font-size: 20px;
    font-weight: 400;
    margin-bottom: 5px;

    a {
      color: inherit;
      text-decoration: none;
      font-size: 15px;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  h5 {
    font-size: 18px;
    margin-bottom: 12px;
  }

  p {
    margin-top: 0;
  }

  button,
  .button {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;

    &.default {
      background-color: #eee;
      color: #999;

      &:hover,
      &.selected {
        background-color: #ccc;
        color: #333;
      }
    }
  }

  header {
    width: 100%;
    padding: 15px;
    background: black;
    color: white;
    z-index: 3;

    h1.header-logo {
      float: left;
      margin: 0;
      width: 60%;
      text-align: left;
      color: inherit;
      font-size: inherit;
      font-family: inherit;
      font-weight: inherit;
      line-height: inherit;
      text-transform: lowercase;
      -webkit-text-stroke: inherit;
      font-feature-settings: inherit;

      > a {
        color: white;
        text-decoration: none;

        > img {
          height: 50px;
          vertical-align: middle;
        }

        > span {
          line-height: 50px;
          vertical-align: middle;
          font-size: 36px;
        }
      }
    }

    .header-apply {
      float: right;
      text-align: right;
      width: 40%;

      .button {
        margin: 5px 0;
      }
    }

    @media screen and (max-width: 649px) {
      text-align: center;

      .header-logo,
      .header-apply {
        float: none;
        text-align: center;
        width: 100%;
      }

      .header-logo {
        margin-bottom: 20px;
      }
    }
  }

  .splash {
    background-color: #3c6d78;
    background-position: center center;
    background-repeat: no-repeat;
    background-size: cover;
    position: relative;
    width: 100%;

    &:before {
      content: '';
      display: block;
    }

    > div {
      background-repeat: no-repeat;
      background-color: transparent;
      background-size: cover;
      background-position: center center;
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
    }

    @media screen and (max-width: 649px) {
      &:before {
        padding-top: 37%;
      }

      &.jobs {
        > div {
          background-image: url('https://nuts.com/images/email/2024/careers_people_md.673a1d85.jpg');
        }
      }
    }

    @media screen and (min-width: 650px) {
      &:before {
        padding-top: 25%;
      }

      &.jobs {
        background-image: url('https://nuts.com/images/email/2024/careers_people_md.673a1d85.jpg');

        > div {
          background-image: url('https://nuts.com/images/email/2024/careers_people.2f0f957d.jpg');
        }
      }
    }
  }

  .container {
    max-width: 800px;
    margin: 0 auto;
    padding: 25px 15px;
  }

  .apply {
    margin-top: 40px;
    text-align: center;
  }

  .nutty-intro {
    p {
      font-weight: 700;
      font-size: 26px;
      font-family: sofia-pro, sans-serif;
    }

    ul {
      margin-left: 10px;
    }
  }

  .sort-title {
    float: left;
    width: 150px;

    @media screen and (max-width: 649px) {
      font-size: 15px;
      width: 132px;
    }
  }

  .sort-groups {
    float: left;

    > button {
      color: #cf5702;
      display: inline-block;
      margin: 0 0 15px 15px;

      @media screen and (max-width: 649px) {
        display: block;
        font-size: 12px;
        margin: 5px 0 15px 0;
        padding: 5px 15px;
      }
    }
  }

  .positions {
    .job {
      margin-bottom: 40px;

      h4 {
        float: left;
        margin-top: 0;
      }

      .learn-more {
        float: right;
        margin-bottom: 5px;
        text-align: right;
        width: 130px;

        > a {
          margin-bottom: 0;
        }

        @media screen and (max-width: 649px) {
          margin-bottom: -27px;
          margin-left: 10px;
        }
      }

      .description {
        font-size: 16px;
      }
    }

    ul {
      margin: 16px 0;
      padding-left: 40px;
    }

    li {
      list-style: disc;
      margin-bottom: 4px;
    }
  }

  .tags {
    > span {
      display: inline-block;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 1px;
      margin-right: 30px;
      text-align: center;
      text-transform: uppercase;

      &:last-child {
        margin-right: 0;
      }

      @media screen and (max-width: 649px) {
        display: block;
        margin-right: 0;
      }
    }

    &.list {
      @media screen and (max-width: 649px) {
        > span {
          margin-right: 130px;
          text-align: left;
        }
      }
    }
  }

  p.tags {
    margin-bottom: 20px;
    text-align: center;
  }

  .logos {
    margin: 0 auto;
    padding: 0 15px;
    max-width: 1280px;
    text-align: center;

    > img {
      margin: 10px;
      vertical-align: middle;
    }
  }

  .return {
    .button {
      margin-left: 0;
    }

    @media screen and (max-width: 649px) {
      text-align: center;
    }
  }
}
</style>
